import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { container } from '@/services/container';
import { useIdentityStore } from '@/store/identity';
import { useUiStore } from '@/store/ui';
import { MeshLinkError } from '@/lib/result';
import { invalidMnemonicWords, isValidMnemonic, normalizeMnemonic } from '@/lib/validate';

/** 导入页：12 词恢复身份 + 词表校验。 */
export function ImportPage(): JSX.Element {
  const navigate = useNavigate();
  const setUnlocked = useIdentityStore((s) => s.setUnlocked);
  const pushError = useUiStore((s) => s.pushError);
  const pushToast = useUiStore((s) => s.pushToast);

  const [raw, setRaw] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const words = useMemo(() => normalizeMnemonic(raw), [raw]);
  const unknown = useMemo(() => invalidMnemonicWords(words), [words]);
  const valid = isValidMnemonic(words);
  const canSubmit = valid && password.length >= 6 && !submitting;

  const submit = async (): Promise<void> => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const meta = await container.identity.importMnemonic(words, password);
      setUnlocked(meta);
      pushToast('success', '身份已恢复，DID 与原来完全一致');
      navigate('/chat', { replace: true });
    } catch (err) {
      pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setSubmitting(false);
      setPassword('');
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <div className="card w-[min(92vw,520px)] p-6">
        <h1 className="text-lg font-semibold text-text">导入已有身份</h1>
        <p className="mt-1 text-sm text-dim">输入 12 个助记词（空格分隔），并设置一个本机解锁口令。</p>

        <div className="mt-5 space-y-4">
          <Textarea
            placeholder="abandon ability able about above absent absorb abstract absurd abuse access accident"
            value={raw}
            invalid={words.length > 0 && !valid}
            onChange={(e) => setRaw(e.target.value)}
          />

          <div className="flex items-center justify-between text-xs">
            <span className={valid ? 'text-ok' : 'text-dim'}>
              已识别 {words.length} 词{valid ? ' · 校验通过 ✓' : ''}
            </span>
            {unknown.length > 0 ? <span className="text-err">无效词：{unknown.join(', ')}</span> : null}
          </div>

          <Input
            type="password"
            placeholder="设置本机解锁口令（至少 6 位）"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <Button block loading={submitting} disabled={!canSubmit} onClick={() => void submit()}>
            恢复身份
          </Button>
        </div>

        <div className="mt-4 flex justify-between text-xs">
          <button type="button" className="text-dim hover:text-accent" onClick={() => navigate('/welcome')}>
            返回
          </button>
          <span className="text-dim">助记词只在本地参与派生，不会上传</span>
        </div>
      </div>
    </div>
  );
}

export default ImportPage;
